import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material'
import { chamberRoomAuditAPI, AUDIT_ERROR_CODES } from 'src/supportPortal/services/chamberRoomAuditService'
import {
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'

export default function ApproveRejectDialog({ open, onClose, request, onSuccess }) {
  const [decision, setDecision] = useState('APPROVED')
  const [comment, setComment] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  React.useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setDecision('APPROVED')
      setComment('')
      setExpiryDate('')
      setError(null)
    }
  }, [open])

  const handleSubmit = async () => {
    if (!request?.id) {
      setError('Request ID is missing')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Convert expiry date to ISO-8601 UTC format if provided
      let approvalExpiry = null
      if (expiryDate && decision === 'APPROVED') {
        const date = new Date(expiryDate)
        if (!isNaN(date.getTime())) {
          approvalExpiry = date.toISOString()
        }
      }

      const response = await chamberRoomAuditAPI.approveOrRejectAuditAccess(
        request.id,
        decision,
        comment,
        approvalExpiry
      )

      if (response && isCommonResponseSuccessfulV2(response)) {
        toast.success(`Request ${decision === 'APPROVED' ? 'approved' : 'rejected'} successfully`)
        onSuccess?.()
      } else {
        const errorCode = response?.response?.data?.code
        const errorMessage = AUDIT_ERROR_CODES[errorCode] || 'Failed to update request'
        setError(errorMessage)
        
        // If request is no longer pending (error 455), refresh and show message
        if (errorCode === 455) {
          toast.warning('Request is no longer pending')
          setTimeout(() => {
            onSuccess?.()
          }, 1500)
        }
      }
    } catch (err) {
      console.error('Error approving/rejecting request:', err)
      const errorCode = err?.response?.data?.code
      const errorMessage = AUDIT_ERROR_CODES[errorCode] || 'An error occurred while updating the request'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  // Get minimum datetime for expiry (current time)
  const getMinDateTime = () => {
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    return now.toISOString().slice(0, 16)
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {decision === 'APPROVED' ? 'Approve' : 'Reject'} Audit Request
        </Typography>
        {request?.id && (
          <Typography variant="caption" color="text.secondary">
            Request ID: {request.id}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Decision Selection */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
              Decision *
            </FormLabel>
            <RadioGroup
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              row
            >
              <FormControlLabel
                value="APPROVED"
                control={<Radio />}
                label="Approve"
              />
              <FormControlLabel
                value="REJECTED"
                control={<Radio />}
                label="Reject"
              />
            </RadioGroup>
          </FormControl>

          {/* Comment */}
          <TextField
            fullWidth
            label="Comment (Optional)"
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter approval or rejection comment..."
            sx={{ mb: 3 }}
            inputProps={{ maxLength: 500 }}
            helperText={`${comment.length}/500 characters`}
          />

          {/* Expiry Date (only for approval) */}
          {decision === 'APPROVED' && (
            <TextField
              fullWidth
              type="datetime-local"
              label="Expiry Date (Optional)"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for permanent access"
              sx={{ mb: 2 }}
              inputProps={{
                min: getMinDateTime(),
              }}
            />
          )}

          {decision === 'REJECTED' && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Note: Expiry date is not applicable for rejected requests.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !request?.id}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ textTransform: 'none' }}
        >
          {loading
            ? 'Processing...'
            : decision === 'APPROVED'
            ? 'Approve'
            : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
